import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck, Mail, KeyRound, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { API_V1 } from "@/lib/api";

const RESEND_COOLDOWN_SEC = 30;

export default function Login() {
  const [stage, setStage] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [inlineError, setInlineError] = useState(""); // e.g. "Invalid code. 2 attempts left."
  const [exhausted, setExhausted] = useState(false); // 3/3 used → must resend
  const [resendIn, setResendIn] = useState(0); // seconds remaining before next resend
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid work email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_V1}/auth/request-otp`, { email: email.trim().toLowerCase() });
      setStage("code");
      setCode("");
      setInlineError("");
      setExhausted(false);
      setResendIn(RESEND_COOLDOWN_SEC);
      toast.success("We've sent a 6-digit code to your inbox.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send code. Please retry.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setLoading(true);
    setInlineError("");
    try {
      const res = await axios.post(`${API_V1}/auth/verify-otp`, {
        email: email.trim().toLowerCase(),
        code,
      });
      login(res.data);
      toast.success("Welcome back.");
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Invalid code.";
      // Inline (don't toast — it's already on screen)
      setInlineError(msg);
      if (status === 429 || /Send a new one/i.test(msg)) {
        setExhausted(true);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left column — form */}
      <div className="flex flex-col px-8 sm:px-14 lg:px-20 py-10 lg:py-14 bg-white">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center font-display font-bold tracking-tight">
            A
          </div>
          <div>
            <div className="font-display text-xl leading-none font-bold text-slate-900">Axia</div>
            <div className="axia-overline mt-1">Performance, aligned.</div>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            {stage === "email" ? (
              <>
                <div className="axia-overline mb-3 text-slate-500">Sign in</div>
                <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                  Your team's
                  <br />
                  performance home.
                </h1>
                <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                  Enter your work email. We'll send a one-time code &mdash; no passwords to remember.
                </p>

                <form onSubmit={requestOtp} className="mt-10 space-y-4" data-testid="email-form">
                  <label className="block">
                    <span className="axia-overline">Work email</span>
                    <div className="mt-2 relative">
                      <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        type="email"
                        autoFocus
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12 pl-9 text-base bg-white border-slate-300"
                        data-testid="login-email-input"
                      />
                    </div>
                  </label>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm tracking-wide group"
                    data-testid="login-send-code-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Send sign-in code
                    <ArrowRight className="w-4 h-4 ml-2 transition group-hover:translate-x-0.5" />
                  </Button>
                </form>
              </>
            ) : (
              <>
                <div className="axia-overline mb-3 text-slate-500">Verify</div>
                <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                  Check your inbox.
                </h1>
                <p className="mt-4 text-sm text-slate-500 leading-relaxed">
                  We sent a 6-digit code to <span className="font-medium text-slate-800">{email}</span>. The code expires in 10 minutes.
                </p>

                <form onSubmit={verifyOtp} className="mt-10 space-y-5" data-testid="otp-form">
                  <label className="block">
                    <span className="axia-overline flex items-center gap-1.5">
                      <KeyRound className="w-3 h-3" /> Sign-in code
                    </span>
                    <div className="mt-3" data-testid="login-otp-input">
                      <InputOTP maxLength={6} value={code} onChange={setCode} disabled={exhausted}>
                        <InputOTPGroup className="gap-2">
                          {[0, 1, 2, 3, 4, 5].map((i) => (
                            <InputOTPSlot
                              key={i}
                              index={i}
                              className="w-12 h-14 text-2xl font-mono border-slate-300 rounded-md"
                            />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </label>

                  {inlineError && (
                    <div
                      className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs leading-relaxed ${
                        exhausted
                          ? "border-amber-200 bg-amber-50 text-amber-900"
                          : "border-rose-200 bg-rose-50 text-rose-900"
                      }`}
                      data-testid="login-inline-error"
                    >
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span className="flex-1">{inlineError}</span>
                    </div>
                  )}

                  {exhausted ? (
                    <Button
                      type="button"
                      onClick={requestOtp}
                      disabled={loading || resendIn > 0}
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium tracking-wide"
                      data-testid="login-resend-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      {resendIn > 0 ? `Send a new code in ${resendIn}s` : "Send a new code"}
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={loading || code.length !== 6}
                      className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium tracking-wide"
                      data-testid="login-verify-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Verify and continue
                    </Button>
                  )}

                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => {
                        setStage("email");
                        setCode("");
                        setInlineError("");
                        setExhausted(false);
                      }}
                      className="text-slate-500 hover:text-slate-900 underline underline-offset-4"
                      data-testid="login-change-email"
                    >
                      Use a different email
                    </button>
                    {!exhausted && (
                      <button
                        type="button"
                        onClick={requestOtp}
                        disabled={loading || resendIn > 0}
                        className="text-slate-500 hover:text-slate-900 underline underline-offset-4 disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                        data-testid="login-resend-link"
                      >
                        {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                      </button>
                    )}
                  </div>
                </form>
              </>
            )}
          </div>
        </div>

        <footer className="text-[11px] text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1 mt-8">
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Axia never stores compensation data.
          </span>
          <span>WCAG 2.1 AA</span>
          <span>SOC 2 in progress</span>
        </footer>
      </div>

      {/* Right column — hero image */}
      <div className="hidden lg:block relative bg-slate-100 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1600&q=80"
          alt="Bright modern open-plan office"
          className="w-full h-full object-cover axia-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-slate-950/10" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="axia-overline text-white/60">Performance management, reimagined</div>
          <p className="font-display text-3xl font-bold tracking-tight mt-3 max-w-md leading-[1.15]">
            The system of record for every objective, every check-in, every career story.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-md">
            <Stat label="Frameworks" value="BSC · OKR · SMART" />
            <Stat label="Compensation" value="Never stored" />
            <Stat label="Auth" value="Passwordless" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40">{label}</div>
      <div className="text-xs font-mono mt-1 text-white/80">{value}</div>
    </div>
  );
}
